import { useEffect, useState } from "react";
import { MainLayout } from "../../layout/MainLayout";
import { Button, Form, InputGroup } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import HelpAdd from "./HelpAdd";
import { Help, emptyHelp } from "../../interfaces/Help";
import HelpDelete from "./HelpDelete";
import HelpEdit from "./HelpEdit";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../elements/LoadingSpinner";
import { handleApiError, safeJsonParse } from "../../../utils/api";

export default function Helps() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('isAdmin');

    const [helps, setHelps] = useState<Help[]>([]);
    const [selectedHelp, setSelectedHelp] = useState<Help>(emptyHelp);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    // Search and sort states
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('weight');
    const [isSearching, setIsSearching] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const columns: TableColumn<Help>[] = [
        {
            name: t("Id"),
            selector: (row: Help) => row._id,
            sortable: true,
            width: '200px',
            omit: true,
        },
        {
            name: t("Name"),
            selector: (row: Help) => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: t("Weight"),
            selector: (row: Help) => row.weight,
            sortable: true,
            width: '100px',
        },
        {
            name: t("Help items"),
            selector: (row: Help) => {
                const itemsWithEnglish = row.items.filter(item => {
                    return item.translations.some(t => t.lang === item.defaultLang);
                });
                return itemsWithEnglish.length;
            },
            sortable: false,
            width: '120px',
        },
        {
            name: t("Languages"),
            selector: (row: Help) => {
                const languages = new Set<string>();
                row.items.forEach(item => {
                    item.translations.forEach(trans => {
                        languages.add(trans.lang.toUpperCase());
                    });
                });
                return Array.from(languages).sort().join(', ') || '-';
            },
            sortable: false,
            width: '150px',
        },
        {
            name: t("Actions"),
            cell: (row: Help) => (
                <div className="actions">
                    <a
                        className="edit-link"
                        title={t("Edit help")}
                        href="#"
                        onClick={(event) => handleShowEditForm(event, row._id)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete help")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const ExpandedComponent = ({ data }: { data: Help }) => (
        <div style={{ padding: '15px' }}>
            <div><strong>{t("Id")}:</strong> {data._id}</div>
        </div>
    );

    const fetchHelps = () => {
        const params = new URLSearchParams();
        if (searchQuery.trim()) {
            params.append('search', searchQuery.trim());
        }
        if (sortBy) {
            params.append('sort', sortBy);
        }

        const queryString = params.toString();
        const url = import.meta.env.VITE_API_URL + '/v1/help' + (queryString ? '?' + queryString : '');

        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };

        setLoading(true);
        setIsSearching(true);
        fetch(url, requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setHelps(data);
                setLoading(false);
                setIsSearching(false);
            })
            .catch(() => {
                setLoading(false);
                setIsSearching(false);
            });
    };

    useEffect(() => {
        fetchHelps();
    }, [sortBy, reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (h: Help) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchHelps();
    }

    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value);
    }

    const handleClearFilters = () => {
        setSearchQuery('');
        setSortBy('weight');
        // fetchHelps will be called by useEffect when sortBy changes
    }

    const handleToggleFilters = () => {
        setShowFilters(!showFilters);
    }

    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedHelp: Help = helps.find((h: Help) => h._id == id) ?? emptyHelp;
        if (newSelectedHelp._id != '') {
            setSelectedHelp(newSelectedHelp);
            setShowDeleteForm(true);
        } else {
            alert(t("No help selected."));
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowEditForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedHelp: Help = helps.find((h: Help) => h._id == id) ?? emptyHelp;
        if (newSelectedHelp._id != '') {
            setSelectedHelp(newSelectedHelp);
            setShowEditForm(true);
        } else {
            alert(t("No help selected."));
        }
    }
    
    const handleHideEditForm = () => {
        setShowEditForm(false);
    }

    const handleSumbitEditForm = (help: Help) => {
        setShowEditForm(false);
        setReloadData(prev => prev + 1);
    }

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    { isAdmin &&
                        <div className="admin-helps-container">
                                    <div className="page-header">
                                        <h1>{ t("Help management") }</h1>
                                        <div className="page-actions">
                                            <button
                                                className={`filter-toggle ${showFilters ? 'active' : 'inactive'}`}
                                                onClick={handleToggleFilters}
                                                title={t("Filters")}
                                            >
                                                <i className="bi bi-funnel"></i>
                                            </button>
                                            <a className="action-icon" onClick={ handleShowAddForm } title={ t("New help") }>
                                                <i className="bi bi-plus-circle"></i>
                                            </a>
                                        </div>
                                    </div>

                                    {/* Search and filter controls */}
                                    <div className={`help-filters ${!showFilters ? 'hidden' : ''}`}>
                                        <Form onSubmit={handleSearchSubmit}>
                                            <div className="filter-controls">
                                                <Form.Group className="search-group">
                                                    <Form.Label>{ t("Search") }</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder={ t("Search in all fields...") }
                                                            value={searchQuery}
                                                            onChange={handleSearchChange}
                                                        />
                                                        <Button variant="primary" type="submit" disabled={isSearching}>
                                                            {isSearching ? t("Searching...") : t("Search")}
                                                        </Button>
                                                    </InputGroup>
                                                </Form.Group>

                                                <Form.Group className="sort-group">
                                                    <Form.Label>{ t("Sort by") }</Form.Label>
                                                    <Form.Select value={sortBy} onChange={handleSortChange}>
                                                        <option value="weight">{ t("Weight") } (↑)</option>
                                                        <option value="-weight">{ t("Weight") } (↓)</option>
                                                        <option value="name">{ t("Name") } (A-Z)</option>
                                                        <option value="-name">{ t("Name") } (Z-A)</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                {(searchQuery || sortBy !== 'weight') && (
                                                    <Button variant="secondary" onClick={handleClearFilters}>
                                                        { t("Clear filters") }
                                                    </Button>
                                                )}
                                            </div>
                                        </Form>

                                        <div className="filter-info">
                                            { t("Total results") }: <strong>{helps.length}</strong>
                                            {searchQuery && (
                                                <span className="search-term">
                                                    { t("Searching for") }: <strong>"{searchQuery}"</strong>
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="admin-helps-table-container">
                                        <DataTable
                                            columns={columns}
                                            data={helps}
                                            progressPending={loading}
                                            progressComponent={<LoadingSpinner />}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                            paginationComponentOptions={{
                                                rowsPerPageText: t('Rows per page') + ':',
                                                rangeSeparatorText: t('of'),
                                            }}
                                            highlightOnHover
                                            striped
                                            responsive
                                            noDataComponent={t("No help items found")}
                                            expandableRows
                                            expandableRowsComponent={ExpandedComponent}
                                        />
                                    </div>
                                </div>
                            }
                    { !isAdmin &&
                        <div className="admin-helps-container">
                            { t("You are not allowed to access this section.") }
                        </div>
                    }
                </div>
            </div>
            { showAddForm &&
                <HelpAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            { showDeleteForm &&
                <HelpDelete help={ selectedHelp } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
            { showEditForm &&
                <HelpEdit help={ selectedHelp } submitCallback={ handleSumbitEditForm } closeCallback={ handleHideEditForm } />
            }
        </MainLayout>);
}